import React, { CSSProperties, useState, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { Dropdown, Layout, Menu, Button, Avatar, Image } from 'antd';
import { UserOutlined, LogoutOutlined } from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useIsMobile } from 'lib/utils';
import Sidebar from 'components/layouts/Sidebar';
import { SIDEBAR_WIDTH } from 'config/constant';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { logout, selectAuth } from 'redux/slice/authSlice';
import type { MenuProps } from 'antd';
import Logo from 'assets/images/terra-charge-logo-sidebar.png';

const { Header, Content } = Layout;

type Props = {
    containerClass?: string;
    contentStyle?: CSSProperties;
};

/**
 * Creates Base Layout
 * @param { Props } props
 * @return html
 * */
const BaseLayout = (props: Props) => {
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
    const { containerClass = '', contentStyle } = props;
    const onCloseCollapsed = useCallback(() => isMobile && setIsCollapsed(true), [isMobile]);
    const dispatch = useAppDispatch();
    const { token, name } = useAppSelector(selectAuth);
    const location = useLocation();

    const onLogout = () => {
        dispatch(logout());
    };

    const onGotoProfile = () => navigate('/detail-profile');

    const accountActionItems: MenuProps['items'] = [
        {
            icon: <LogoutOutlined className='fs-6 me-2' />,
            label: 'ログアウト',
            key: 'logout',
        },
    ];

    const onMenuClick: MenuProps['onClick'] = ({ key }) => {
        switch (key) {
            case 'profile':
                onGotoProfile();
                break;
            case 'logout':
                onLogout();
                break;
            default:
                break;
        }
    };

    const redirectHome = () => {
        navigate('/');
    };

    useEffect(() => {
        if (!token) {
            navigate('/login', { state: { from: location }, replace: true });
        }
    }, [token, location, navigate]);

    return (
        <Layout hasSider className='base-layout-container h-100'>
            {isMobile ? (
                <Sidebar
                    key={+isMobile}
                    breakpoint='md'
                    collapsedWidth='0'
                    collapsed={isCollapsed}
                    onCollapse={(collapsed) => setIsCollapsed(collapsed)}
                    onCloseCollapsed={onCloseCollapsed}
                    setIsCollapsed={setIsCollapsed}
                />
            ) : !isCollapsed ? (
                <Sidebar
                    key={+isMobile}
                    breakpoint='md'
                    collapsedWidth='0'
                    collapsed={isCollapsed}
                    onCollapse={(collapsed) => setIsCollapsed(collapsed)}
                    onCloseCollapsed={onCloseCollapsed}
                    setIsCollapsed={setIsCollapsed}
                />
            ) : (
                // <Image className='hamburgerMenu' onClick={() => setIsCollapsed(!isCollapsed)} src={Hamburger} preview={false} />
                <Sidebar
                    key={+isMobile}
                    breakpoint='md'
                    collapsedWidth='100px'
                    collapsed={isCollapsed}
                    onCollapse={(collapsed) => setIsCollapsed(!collapsed)}
                    onCloseCollapsed={onCloseCollapsed}
                    setIsCollapsed={setIsCollapsed}
                />
            )}

            <Layout
                className={!isCollapsed && !isMobile ? 'section-collapsed site-layout' : 'site-layout'}
                // className='site-layout'
                style={{ marginLeft: isMobile ? 0 : !isCollapsed ? SIDEBAR_WIDTH : 0 }}
                onClick={onCloseCollapsed}
            >
                <Header
                    className={
                        !isCollapsed && !isMobile
                            ? 'data-collapsed site-layout-sub-header-background site-default-header position-sticky top-0 '
                            : 'site-layout-sub-header-background site-default-header position-sticky top-0 '
                    }
                >
                    <Image src={Logo} width='217px' preview={false} onClick={redirectHome} />
                    <div className='float-end'>
                        <Dropdown
                            arrow
                            overlay={<Menu onClick={onMenuClick} className='rounded-3 px-1' items={accountActionItems} />}
                            placement='bottomRight'
                            trigger={['click']}
                        >
                            <Button type='link' className='text-white btn-toggle-account-menu'>
                                <Avatar icon={<UserOutlined />} size='small' className={'mx-2'} />
                                {name}
                            </Button>
                        </Dropdown>
                    </div>
                </Header>
                <Content
                    className={
                        isCollapsed
                            ? classNames('main-container-collapse ml-100', containerClass)
                            : classNames('main-container background-white content-collapsed', containerClass)
                    }
                    style={contentStyle}
                >
                    <div className='site-layout-background'>
                        <Outlet />
                    </div>
                </Content>
            </Layout>
        </Layout>
    );
};

export default BaseLayout;
